/**
 * Resolve the subjects an educator selected during onboarding into `{ id, name }` objects.
 * The onboarding API returns the picks as ids (`selected.subjects`) and the catalog separately (`options.subjects`).
 * @param {Object} [onboarding] - Response body of the onboarding endpoint
 * @returns {Array<{id: string, name: string}>} Selected subjects, in catalog order
 */
export const getSelectedSubjects = (onboarding) => {
    const selectedIds = onboarding?.selected?.subjects ?? [];
    const catalog = onboarding?.options?.subjects ?? [];

    return catalog.filter(({ id }) => selectedIds.includes(id));
};

// Normalized onboarding subject names -> subject keys.
const SUBJECT_KEYS = {
    science: 'Science',
    'english/language arts': 'ELA',
    'english language arts': 'ELA',
    ela: 'ELA',
    math: 'Math',
    mathematics: 'Math',
    'social studies': 'Social Studies',
    'visual and performing arts': 'Visual and Performing Arts',
    'health and wellness': 'Health and Wellness',
    'research/study skills': 'Research/Study Skills',
    'career/workplace skills': 'Career/Workplace Skills',
};

// Subjects -> areas of expertise (the `tag` codes in `tags()` of `@/data/formData`).
export const SUBJECT_TO_EXPERTISE_TAGS = {
    Science: ['life-science', 'physical-science', 'earth-science', 'engineering', 'computer-science', 'health'],
    ELA: ['english', 'visual-arts'],
    'Social Studies': ['social-studies', 'government', 'financial', 'business'],
    Math: ['mathematics', 'data'],
    'Visual and Performing Arts': ['visual-arts'],
    'Health and Wellness': ['health'],
    'Research/Study Skills': ['general', 'data'],
    'Career/Workplace Skills': ['general', 'financial', 'business'],
};

/**
 * Map a subject name received from the onboarding endpoint to its Core 4 subject key.
 * @param {string} [subjectName] - Subject name, e.g. `Mathematics`
 * @returns {string|undefined} a subject key such as `Science` or `Health and Wellness`; undefined for an unrecognized subject
 */
export const getSubjectKey = (subjectName) => SUBJECT_KEYS[subjectName?.trim().toLowerCase()];

/**
 * Map a subject name received from the onboarding endpoint to its areas of expertise.
 * @param {string} [subjectName] - Subject name, e.g. `Mathematics`
 * @returns {string[]} Expertise tag codes; empty when the subject is not recognized
 */
export const getExpertiseTagsForSubject = (subjectName) => {
    const key = getSubjectKey(subjectName);

    return key ? [...SUBJECT_TO_EXPERTISE_TAGS[key]] : [];
};
